import _extends from "@babel/runtime/helpers/esm/extends";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
/**
 * URL Provider for Deezer's media
 */
// Enums
import { MediaErrorCode, ResponseErrorCode } from "./types.js"; // Errors
import { APIDeprecatedError, ExpiredLicenseTokenError, ExpiredTrackTokenError, InvalidLicenseTokenError, InvalidResponseError, InvalidTrackTokenError, NoFormatRequestedError, NoLicenseTokenError, NoNetworkError, NoTrackTokenError, NoURLForMediaServiceError, ParsingError, ServerError, TimeoutError, UnauthorizedLicenseTokenError, UnauthorizedTrackTokenError, UnknownCodeError, UnknownMediaError } from "./DeezerMediaURLProviderErrors.js"; // Types guards
import { isMediaResponseErrored, isServiceResponseErrored } from "./types.js"; // Types
/**
 * Constants
 */
const VERSION = 1; // Current media-service API version

/**
 * Types
 */
var _endpoint = /*#__PURE__*/_classPrivateFieldLooseKey("endpoint");
var _fetch = /*#__PURE__*/_classPrivateFieldLooseKey("fetch");
var _timeService = /*#__PURE__*/_classPrivateFieldLooseKey("timeService");
export default class DeezerMediaURLProvider {
  constructor(...[origin, licenseToken, {
    fetch = globalThis.fetch,
    timeService = undefined
  } = {}]) {
    this.licenseToken = void 0;
    Object.defineProperty(this, _endpoint, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _fetch, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _timeService, {
      writable: true,
      value: void 0
    });
    if (!origin) {
      throw new NoURLForMediaServiceError();
    }
    _classPrivateFieldLooseBase(this, _endpoint)[_endpoint] = `${origin}/v${VERSION}/get_url`;
    _classPrivateFieldLooseBase(this, _fetch)[_fetch] = fetch;
    _classPrivateFieldLooseBase(this, _timeService)[_timeService] = timeService;
    this.licenseToken = licenseToken;
  }
  getURLs(tracks, requests) {
    var _this = this;
    return _asyncToGenerator(function* () {
      var _this$licenseToken, _classPrivateFieldLoo;
      // Make a copy of the order to avoid unwanted modification while fetching the media-service
      tracks = [...tracks];
      requests = Array.isArray(requests) ? requests : [requests];
      if (!((_this$licenseToken = _this.licenseToken) != null && _this$licenseToken.toString())) {
        throw new NoLicenseTokenError(undefined, {
          token: _this.licenseToken
        });
      }
      if (!tracks.length) {
        throw new NoTrackTokenError(undefined, {
          tracks
        });
      }
      if (tracks.some(({
        token
      }) => !(token != null && token.toString()))) {
        throw new NoTrackTokenError(undefined, {
          tracks: tracks.filter(track => {
            var _track$token;
            return !((_track$token = track.token) != null && _track$token.toString());
          })
        });
      }
      const now = (_classPrivateFieldLoo = _classPrivateFieldLooseBase(_this, _timeService)[_timeService]) == null ? void 0 : _classPrivateFieldLoo.now();
      if (tracks.some(({
        token
      }) => token.isExpired(now))) {
        throw new ExpiredTrackTokenError(undefined, {
          tracks: tracks.filter(track => !track.token.isExpired(now))
        });
      }
      if (!requests.length) {
        throw new NoFormatRequestedError(undefined, {
          requests,
          tracks
        });
      }
      const body = {
        license_token: _this.licenseToken.toString(),
        media: requests,
        track_tokens: tracks.map(({
          token
        }) => token.toString())
      };
      const init = {
        body: JSON.stringify(body),
        headers: {
          'content-type': 'application/json'
        },
        method: 'POST'
      };
      const request = {
        license_token: body.license_token,
        media: body.media
      }; // For logging only
      const response = yield _classPrivateFieldLooseBase(_this, _fetch)[_fetch].call(null, _classPrivateFieldLooseBase(_this, _endpoint)[_endpoint], init).catch(error => {
        if (error instanceof TypeError) {
          throw typeof navigator !== 'undefined' && navigator.onLine ? new TimeoutError(error.message, {
            request,
            tracks
          }) : new NoNetworkError(error.message, {
            request,
            tracks
          });
        }
        throw error;
      });
      if (!response) {
        throw new ServerError('Cannot retrieve the response', {
          request,
          tracks
        });
      }
      const date = response.headers.get('Date');
      if (date) {
        var _classPrivateFieldLoo2;
        (_classPrivateFieldLoo2 = _classPrivateFieldLooseBase(_this, _timeService)[_timeService]) == null || _classPrivateFieldLoo2.update(date);
      }

      // Throw when receiving an HTTP error (but only for 500+, we might have an usuable body with 4XX)
      if (!response.ok && response.status >= 500) {
        throw Object.assign(new ServerError(`Got an HTTP code ${response.status}`, {
          request,
          response: yield response.text().catch(error => error && error instanceof Error ? `${error.name}: ${error.message}` : 'Failed to read'),
          tracks
        }), {
          code: `MS0${response.status}`
        });
      }
      const raw = yield response.clone().text().catch(error => error && error instanceof Error ? `${error.name}: ${error.message}` : 'Failed to read');
      const json = yield response.clone().json().catch(() => {
        throw new ParsingError('Failed to parse the response', {
          request,
          response: raw,
          tracks
        });
      });
      const details = {
        request,
        response: raw,
        tracks
      };
      if (isServiceResponseErrored(json)) {
        const {
          code = 0,
          message = ''
        } = json.errors[0] || {};
        switch (code) {
          case ResponseErrorCode.API_DEPRECATED:
            throw new APIDeprecatedError(message, _extends({}, details, {
              code
            }));
          case ResponseErrorCode.EXPIRED_LICENSE_TOKEN:
            throw new ExpiredLicenseTokenError(message, _extends({}, details, {
              code
            }));
          case ResponseErrorCode.INVALID_LICENSE_TOKEN:
            throw new InvalidLicenseTokenError(message, _extends({}, details, {
              code
            }));
          case ResponseErrorCode.UNAUTHORIZED_LICENSE_TOKEN:
            throw new UnauthorizedLicenseTokenError(message, _extends({}, details, {
              code
            }));
          default:
            throw new UnknownCodeError(undefined, _extends({}, details, {
              code
            }));
        }
      }

      // Check if we received the same amount of MediaResponse for what we requested
      if (tracks.length !== json.data.length) {
        throw new InvalidResponseError(`Got ${json.data.length} tracks but expected ${tracks.length}`, details);
      }
      return new Map(tracks.map((track, index) => {
        const candidate = json.data[index];
        if (isMediaResponseErrored(candidate)) {
          const {
            errors
          } = candidate;
          const {
            code = undefined
          } = errors[0] || {};
          switch (code) {
            case MediaErrorCode.EXPIRED_TRACK_TOKEN:
              return [track, new ExpiredTrackTokenError(undefined, _extends({}, details, {
                code,
                track
              }))];
            case MediaErrorCode.INVALID_TRACK_TOKEN:
              return [track, new InvalidTrackTokenError(undefined, _extends({}, details, {
                code,
                track
              }))];
            case MediaErrorCode.UNAUTHORIZED_TRACK_TOKEN:
              return [track, new UnauthorizedTrackTokenError(undefined, _extends({}, details, {
                code,
                track
              }))];
            default:
              return [track, new UnknownMediaError(undefined, _extends({}, details, {
                code,
                track
              }))];
          }
        }
        const {
          media = []
        } = candidate;
        return [track, media];
      }));
    })();
  }
}
//# sourceMappingURL=DeezerMediaURLProvider.js.map